import { useLabTechSelectLabTechsQuery } from 'schema'
import { useOptions } from '../../hooks/useOptions'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useGlobalState } from 'context/GlobalStateContext'
import { createStrictContext } from 'utils/createContext'
import { useUserSelectLabel } from '../UserSelect/hooks/useUserSelectLabel'
import { useUserSelectTooltip } from '../UserSelect/hooks/useUserSelectTooltip'
import { useUserDeleted } from '../../../../hooks/useUserDeleted'
import {
  ToLabeledValuesOptions,
  toLabeledValues,
} from '../../utils/labeledValues'
import { LabTechSelectContext, LabTechSelectLabTech } from './types'
import { alphabeticalCompare } from 'shared'

const { Provider, useContextFn } = createStrictContext<LabTechSelectContext>(
  'LabTechSelectContext'
)

/**
 * returns the context associated with the {@link LabTechSelectProvider}
 *
 * @throws if the LabTechSelectProvider is not found
 */
export const useLabTechSelectContext = useContextFn

/**
 * a provider to give access to data and methods associated with the LabTechSelect
 *
 * @param props
 */
const LabTechSelectProvider: React.FC = ({ children }) => {
  const {
    state: { projectGlobalId: globalId },
  } = useGlobalState()
  const userDeleted = useUserDeleted()
  const { data, loading } = useLabTechSelectLabTechsQuery({
    ...DefaultQueryHookOptions,
    skip: !globalId,
    variables: { globalId: globalId! },
  })
  const labTechLabel = ({ name, user }: LabTechSelectLabTech) =>
    user ? user.displayName ?? user.mail : name
  const sortLabTechs = (a: LabTechSelectLabTech, b: LabTechSelectLabTech) => {
    const A = labTechLabel(a)
    const B = labTechLabel(b)
    return alphabeticalCompare(A, B)
  }
  const userTechs = useOptions({
    items: data?.labTechs?.records?.filter(
      ({ user }) => user && !userDeleted(user)
    ),
    value: 'id',
    label: labTechLabel,
    tooltip: ({ user }) => (user?.displayName ? user?.mail : undefined),
    sort: sortLabTechs,
  })
  const genericTechs = useOptions({
    items: data?.labTechs?.records?.filter(({ user }) => !user),
    value: 'id',
    label: labTechLabel,
    sort: sortLabTechs,
  })
  const userLabel = useUserSelectLabel()
  const userTooltip = useUserSelectTooltip()
  const labeledValueOptions: ToLabeledValuesOptions<LabTechSelectLabTech> = {
    value: 'id',
    label: ({ name, user }) => (!!user ? userLabel(user) : name),
    tooltip: ({ user }) => (!!user ? userTooltip(user) : 'Generic'),
    deleted: ({ user }) => (!!user ? userDeleted(user) : undefined),
  }
  return (
    <Provider
      value={{
        loading,
        options: [
          { label: 'User Lab Techs', value: 1, options: userTechs },
          { label: 'Generic Lab Techs', value: 2, options: genericTechs },
        ],
        toLabeledValues: (records) =>
          toLabeledValues(records, labeledValueOptions),
        fromLabeledValues: (labeledValues) =>
          (labeledValues ?? []).map(({ value }) => value as number),
        toLabeledValue: (record) =>
          record
            ? toLabeledValues([record], labeledValueOptions)[0]
            : undefined,
        fromLabeledValue: (labeledValue) => labeledValue?.value as number,
      }}
    >
      {children}
    </Provider>
  )
}

export default LabTechSelectProvider
